import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Cpu, RefreshCw, Lightbulb, GraduationCap, Heart, Users } from "lucide-react";

// Shea's speaking photos
const SPEAKING_PODIUM = "https://customer-assets.emergentagent.com/job_ai-leadership-5/artifacts/76y0rdct_IMG_7420.jpeg";
const SPEAKING_PANEL = "https://customer-assets.emergentagent.com/job_ai-leadership-5/artifacts/edu2uulx_IMG_7418.jpeg";

const speakingTopics = [
  {
    icon: Cpu,
    title: "AI & Future of Work",
    description: "Navigate the AI revolution with practical strategies for professionals and organizations.",
    span: "md:col-span-4",
  },
  {
    icon: RefreshCw,
    title: "Reinvention in AI Era",
    description: "How to pivot, adapt, and evolve when everything is changing.",
    span: "md:col-span-4",
  },
  {
    icon: Lightbulb,
    title: "Entrepreneurship",
    description: "Building successful ventures in a rapidly evolving technological landscape.",
    span: "md:col-span-4",
  },
  {
    icon: GraduationCap,
    title: "EdTech & Learning",
    description: "Transforming education for the next generation of learners and leaders.",
    span: "md:col-span-6",
  },
  {
    icon: Heart,
    title: "Building Resilience",
    description: "Developing the mindset and skills to thrive amid uncertainty.",
    span: "md:col-span-6",
  },
  {
    icon: Users,
    title: "Women, Leadership & Transformation",
    description: "Empowering women leaders to drive innovation and organizational change.",
    span: "md:col-span-12",
  },
];

export default function SpeakingSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section
      ref={ref}
      id="speaking"
      className="py-24 md:py-32 lg:py-40 px-6 md:px-12 lg:px-24 relative"
      data-testid="speaking-section"
    >
      {/* Background */}
      <div className="absolute right-0 bottom-0 w-96 h-96 gradient-blob" />
      
      <div className="max-w-7xl mx-auto relative">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <span className="text-xs font-['Orbitron'] uppercase tracking-[0.2em] text-primary mb-4 block">
            Speaking & Events
          </span>
          <h2 className="font-['Orbitron'] text-3xl sm:text-4xl lg:text-5xl font-bold uppercase tracking-tight text-foreground mb-6">
            Topics That <span className="gradient-text">Inspire</span>
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto">
            Shea delivers powerful keynotes, workshops, and panel discussions that leave 
            audiences informed, empowered, and ready to lead.
          </p>
        </motion.div>

        {/* Speaking Photos Grid */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="mb-16 grid grid-cols-1 md:grid-cols-2 gap-6"
        >
          <div className="relative overflow-hidden rounded-xl frame-border">
            <img
              src={SPEAKING_PODIUM}
              alt="Shea speaking at podium"
              className="w-full aspect-video object-cover object-top"
              data-testid="speaking-stage-image-1"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent" />
          </div>
          <div className="relative overflow-hidden rounded-xl frame-border">
            <img
              src={SPEAKING_PANEL}
              alt="Shea at panel discussion"
              className="w-full aspect-video object-cover object-top"
              data-testid="speaking-stage-image-2"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent" />
          </div>
        </motion.div>

        {/* Bento Grid of Topics */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {speakingTopics.map((topic, index) => (
            <motion.div
              key={topic.title}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.3 + index * 0.1 }}
              className={`bento-card p-6 lg:p-8 ${topic.span}`}
              data-testid={`speaking-topic-${index}`}
            >
              <topic.icon className="w-10 h-10 text-primary mb-4" />
              <h3 className="font-['Orbitron'] text-lg font-bold uppercase tracking-wider text-foreground mb-2">
                {topic.title}
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {topic.description}
              </p>
            </motion.div>
          ))}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="text-center mt-12"
        >
          <p className="text-muted-foreground mb-4">
            Available for keynotes, panels, workshops, podcasts, and media appearances.
          </p>
          <a
            href="#speaking-form"
            className="inline-flex items-center text-primary hover:text-white font-bold uppercase tracking-wider transition-colors"
            data-testid="speaking-inquire-link"
          >
            Inquire about booking Shea →
          </a>
        </motion.div>
      </div>
    </section>
  );
}
